import { motion } from "framer-motion";
import { FadeUp } from "./_shared";
import CpiRadar from "./CpiRadar";
import HudCorners from "./_partials/HudCorners";
import { CPI_DOMAINS } from "@/data/cpi";

function DomainBar({ domain, index }) {
  return (
    <div data-testid={`cpi-domain-${domain.code.toLowerCase()}`} className="group">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0 flex items-baseline gap-2.5">
          <span className="font-mono text-[0.6rem] tracking-[0.18em] text-accent font-bold">
            {domain.code}
          </span>
          <span className="font-display text-[0.8rem] md:text-sm font-semibold text-background/90 truncate">
            {domain.name}
          </span>
        </div>
        <span className="font-mono text-xs text-background/70 font-bold">{domain.score}</span>
      </div>
      <div className="mt-1.5 h-1.5 bg-background/10 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${domain.score}%` }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.9, delay: 0.15 + index * 0.05, ease: [0.22, 1, 0.36, 1] }}
          className="h-full bg-accent"
        />
      </div>
    </div>
  );
}

export default function CpiSection() {
  return (
    <section
      id="cpi"
      data-testid="cpi-section"
      className="relative py-24 md:py-32 bg-foreground text-background overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <FadeUp className="max-w-3xl">
          <div className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-background/55">
            <span className="text-accent">§</span> What You Get — The Clinical Performance Index
          </div>
          <h2 className="mt-5 font-display font-bold tracking-tight leading-[1.05] text-[clamp(1.9rem,4.5vw,3.25rem)] text-background">
            See Your Health as <em className="not-italic text-accent">One Connected System</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-background/75 leading-relaxed">
            Instead of looking at individual symptoms in isolation, Integrated Wellness evaluates
            ten interconnected areas of physiology — scored together as your{" "}
            <strong className="text-background">Clinical Performance Index (CPI)</strong>.
          </p>
        </FadeUp>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <FadeUp className="lg:col-span-6">
            <div className="relative border border-background/15 bg-background/[0.03] p-4 md:p-8">
              <HudCorners offset={10} size={12} />
              <div className="flex items-center justify-between px-2">
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-background/50">
                  CPI.SAMPLE · 10 Domains
                </span>
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-accent">
                  Sample Profile
                </span>
              </div>
              <CpiRadar domains={CPI_DOMAINS} dark className="w-full max-w-[460px] mx-auto mt-2" />
            </div>
          </FadeUp>

          <div className="lg:col-span-6">
            <FadeUp delay={0.1} className="space-y-4 md:space-y-[1.15rem]">
              {CPI_DOMAINS.map((d, i) => (
                <DomainBar key={d.code} domain={d} index={i} />
              ))}
            </FadeUp>
          </div>
        </div>

        <FadeUp delay={0.15} className="mt-12 max-w-3xl">
          <p
            data-testid="cpi-explainer"
            className="border-l-2 border-accent pl-5 text-base md:text-lg text-background/70 leading-relaxed"
          >
            Your CPI profile identifies which systems currently represent the greatest optimization
            opportunities — and it updates longitudinally as new labs, wearable data and body
            composition scans become available.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}
